import type { IncidentV3, PulseProblem } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { BTN_BLUE_TEXT, BTN_DARK, BTN_LIGHT, QC, durNoSec, pre, qChip } from './lib';
import { incidentExtra } from './incidentExtra';
import { clockNote, clockOf } from './Station';

const CAP = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;';

/** The problem drawer (design psDrawer, T7507-7526). */
export function StationProblemDrawer({
  q,
  inc,
  now,
  past,
  owner,
  actLabel,
  canWrite,
  writeTitle,
  onAct,
  canAct = true,
  onAck,
  onClose,
  onOpenIncident,
}: {
  q: { p: PulseProblem; remain: number | null; age: number };
  inc: IncidentV3 | null;
  now: number;
  past: boolean;
  owner: string;
  actLabel: string;
  canWrite: boolean;
  writeTitle?: string;
  onAct: () => void;
  /** False for the Developer: the drawer's action is hidden (psCanWrite), Acknowledge stays. */
  canAct?: boolean;
  onAck: () => void;
  onClose: () => void;
  onOpenIncident?: (incidentId: string) => void;
}) {
  const p = q.p;
  const ex = incidentExtra(inc, now);
  const sym = p.symptoms;
  const inert = !canWrite;
  return (
    <>
      <div onClick={(e) => { pre(e); onClose(); }} style={sx('position:fixed;inset:0;z-index:80;background:rgba(21,27,34,.28);')} />
      <div role="dialog" aria-label={p.title} data-screen-label="Pulse · Problem" style={sx('position:fixed;top:0;right:0;bottom:0;z-index:81;width:min(540px,100%);box-sizing:border-box;background:#FCFAF4;border-left:1px solid rgba(34,39,46,.18);box-shadow:-18px 0 40px rgba(21,27,34,.18);overflow-y:auto;padding:16px 18px;display:flex;flex-direction:column;gap:13px;')}>
        <div style={sx('display:flex;align-items:flex-start;gap:10px;min-width:0;')}>
          <span style={sx('flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:4px;')}>
            <span style={sx(qChip(p.severity))}>
              <span>{QC[p.severity][2]}</span>
            </span>
            <span style={sx('font-size:17px;font-weight:800;letter-spacing:-.01em;line-height:1.3;text-wrap:pretty;')}>
              <span>{p.title}</span>
            </span>
          </span>
          <a href="#" onClick={(e) => { pre(e); onClose(); }} style={sx(BTN_LIGHT)}>
            Close · Esc
          </a>
        </div>
        <div style={sx('display:flex;gap:6px 14px;flex-wrap:wrap;font-size:14px;color:#3C454F;')}>
          <span style={sx('font-variant-numeric:tabular-nums;font-weight:800;color:' + (q.remain !== null && q.remain < 30 ? '#B3261E' : '#22272E') + ';')}>
            <span>{clockOf(q.remain, q.age) + ' · ' + clockNote(p, q.remain)}</span>
          </span>
          <span>
            <span>{owner ? 'Owner: ' + owner : 'Unowned'}</span>
          </span>
        </div>
        <div>
          <div style={sx(CAP)}>People</div>
          <div style={sx('font-size:16px;line-height:1.45;margin-top:4px;text-wrap:pretty;font-weight:' + (p.exposed ? '800;color:#B3261E;' : '600;color:#22272E;'))}>
            <span>{p.people}</span>
          </div>
        </div>
        <div>
          <div style={sx(CAP)}>
            <span>{sym.length > 1 ? 'One cause, ' + sym.length + ' symptoms' : 'Symptom'}</span>
          </div>
          {sym.map((t, i) => (
            <div key={i} style={sx('font-size:16px;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
              · <span>{t}</span>
            </div>
          ))}
        </div>
        <div>
          <div style={sx(CAP)}>What changed in the hour before</div>
          {(ex?.changed ?? []).map((c, i) => (
            <div key={i} style={sx('font-size:16px;line-height:1.45;margin-top:4px;')}>
              <span style={sx('color:#54606C;font-variant-numeric:tabular-nums;')}>
                <span>{c.when}</span>
              </span>{' '}
              · <span style={sx('font-weight:700;')}><span>{c.kind}</span></span> · <span>{c.what}</span>{' '}
              <span style={sx('color:#54606C;')}>
                · <span>{c.who}</span>
              </span>
            </div>
          ))}
          <div style={sx('font-size:14px;color:#54606C;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
            <span>{ex ? ex.changedNote : 'No change record linked yet.'}</span>
          </div>
        </div>
        <div>
          <div style={sx(CAP)}>
            Runbook · <span>{ex ? ex.rbName : 'none linked'}</span>
          </div>
          {(ex?.rbSteps ?? []).map((r) => (
            <div key={r.n} style={sx('font-size:16px;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
              <span>{r.n}</span>. <span>{r.t}</span>
            </div>
          ))}
          <div style={sx('font-size:14px;color:#54606C;margin-top:4px;')}>
            <span>{ex ? ex.rbMeta : 'Every alert needs a runbook before it can page.'}</span>
          </div>
        </div>
        <div>
          <div style={sx(CAP)}>Timeline</div>
          {p.timeline
            .filter((e) => Date.parse(e.at) <= now)
            .map((e, i) => (
              <div key={i} style={sx('display:grid;grid-template-columns:minmax(0,70px) minmax(0,1fr);gap:8px;font-size:14px;line-height:1.4;margin-top:4px;')}>
                <span style={sx('font-variant-numeric:tabular-nums;color:#54606C;')}>
                  <span>{'−' + durNoSec((now - Date.parse(e.at)) / 60000)}</span>
                </span>
                <span style={sx('text-wrap:pretty;')}>
                  <span>{e.what}</span>
                </span>
              </div>
            ))}
        </div>
        <div>
          <div style={sx(CAP)}>After it closes</div>
          <div style={sx('font-size:16px;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
            <span>{ex ? ex.review + ' ' + ex.facNote : 'Review due within 5 working days of resolve.'}</span>
          </div>
        </div>
        <div style={sx('display:flex;gap:6px;flex-wrap:wrap;padding-top:10px;border-top:1px solid rgba(34,39,46,.1);')}>
          {!past && (
            <>
              {canAct && (
                <a href="#" aria-disabled={inert ? 'true' : undefined} title={inert ? writeTitle : undefined} onClick={(e) => { pre(e); onAct(); }} style={sx(BTN_DARK)}>
                  <span>{actLabel}</span>
                </a>
              )}
              <a href="#" aria-disabled={inert ? 'true' : undefined} title={inert ? writeTitle : undefined} onClick={(e) => { pre(e); onAck(); }} style={sx(BTN_LIGHT)}>
                <span>{p.acknowledged ? 'Acknowledged' : 'Acknowledge'}</span>
              </a>
            </>
          )}
          {p.incident_id && (
            <a
              href="#"
              onClick={(e) => {
                pre(e);
                onClose();
                onOpenIncident?.(p.incident_id as string);
              }}
              style={sx(BTN_BLUE_TEXT)}
            >
              <span>{'Open ' + (p.incident_ref ?? '') + ' in Setup'}</span>
            </a>
          )}
        </div>
      </div>
    </>
  );
}
